import { useState } from "react";

import Breadcrumb from "../components/Breadcrumb";
import { Button } from "../components/Form/Button";
import { Input } from "../components/Form/Input";
import AuthProvider from "../services/auth/AuthProvider/Index";
import { FormErrorsSchema } from "../types";
import { mergeErrors } from "../utils";
import notifications from "../services/notifications";

const PasswordChange = () => {
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [token, setToken] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrorsSchema>({});

  return (
    <div className="w-full lg:w-2/3 min-h-screen bg-gray-100 text-gray-900">
      <main className="mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <Breadcrumb
          pages={[
            { name: "My account", href: "#", current: false },
            { name: "Password change", href: "#", current: true },
          ]}
        />
        <div className="mt-8">
          <h1 className="text-xl font-semibold">Password change</h1>
        </div>
        <div className="bg-white mt-6 px-6 pb-6 pt-2">
          <div className="w-full">
            <div className="w-full mt-6">
              <Input
                label="New password"
                name="password"
                placeholder=""
                password={true}
                errors={formErrors}
                value={password}
                setValue={setPassword}
                disabled={loading}
                required={true}
              />
            </div>
            <div className="w-full mt-6">
              <Input
                label="Repeat new password"
                name="password_confirmation"
                placeholder=""
                password={true}
                errors={formErrors}
                value={passwordConfirmation}
                setValue={setPasswordConfirmation}
                disabled={loading}
                required={true}
              />
            </div>
            {requested && (
              <div className="w-full mt-6">
                <Input
                  label="Confirmation token"
                  name="token"
                  placeholder=""
                  errors={formErrors}
                  value={token}
                  setValue={setToken}
                  disabled={loading}
                  required={true}
                  inputMask={(inputText: string) => {
                    return inputText.replace(/[^a-zA-Z0-9]/g, "");
                  }}
                />
              </div>
            )}
          </div>

          <div className="w-full mt-24">
            {!requested && (
              <Button
                label="Request password change"
                disabled={loading}
                theme="success"
                size="medium"
                className="block w-full md:ml-auto md:w-auto"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setFormErrors({});

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me/password-change-request`;

                    const response = await fetch(apiUrl, {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
                      },
                      method: "POST",
                      body: JSON.stringify({
                        password: password,
                        password_confirmation: passwordConfirmation,
                      }),
                    });

                    if (!response.ok) {
                      if (response.status === 422) {
                        const errorData = await response.json();
                        setFormErrors(mergeErrors(errorData.errors));
                      } else {
                        throw new Error("Failed to load resource");
                      }
                    }

                    if (response.ok) {
                      notifications.success({message: "Your password change request has been successfully processed. Please take a moment to check your email for the confirmation token."})

                      setRequested(true);
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);
                    
                    notifications.error({message: "Something went wrong while trying to request the password change."});
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            )}
            {requested && (
              <Button
                label="Confirm password change"
                disabled={loading}
                theme="primary"
                size="medium"
                className="block w-full md:ml-auto md:w-auto"
                onClick={async () => {
                  try {
                    setLoading(true);
                    setFormErrors({});

                    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/private/identity/me/password`;

                    const response = await fetch(apiUrl, {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${await AuthProvider.getValidAuthorizationToken()}`,
                      },
                      method: "PATCH",
                      body: JSON.stringify({
                        password: password,
                        password_confirmation: passwordConfirmation,
                        token: token,
                      }),
                    });

                    if (!response.ok) {
                      if (response.status === 422) {
                        const errorData = await response.json();
                        setFormErrors(mergeErrors(errorData.errors));
                      } else {
                        throw new Error("Failed to load resource");
                      }
                    }

                    if (response.ok) {
                      notifications.success({message: "Your password change has been successfully processed."});

                      setRequested(false);
                      setPassword("");
                      setPasswordConfirmation("");
                      setToken("");
                    }
                  } catch (error) {
                    console.error("Error fetching data:", error);

                    notifications.error({message: "Something went wrong while trying to change your password."});
                  } finally {
                    setLoading(false);
                  }
                }}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PasswordChange;
